import React, { useEffect, useState, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import { Calendar, Activity, Heart, Droplet, Database, Underline } from "react-feather"; 
import { useDispatch, useSelector } from 'react-redux';
import { MetabolicsSave, VitalsSave } from '../../apiServices/reducer/dialogApiService';
import confetti from 'canvas-confetti';
import CongratulationsModal from './Congratulations';

function VitalsBPField({ value, onChange, isInvalid, fieldErrors }) {
  const [mask, setMask] = useState('000/00');
  const inputRef = useRef(null);
  const inputErrorStyle = { border: '1px solid #dc3545', boxShadow: 'none' };
  
  const handleChange = (e) => {
    let input = e.target.value;

    // Remove non-numeric and non-slash characters
    input = input.replace(/[^0-9/]/g, '');

    // Automatically insert a slash after the first three digits
    if (input.length > 3 && !input.includes('/')) {
      input = `${input.slice(0, 3)}/${input.slice(3)}`;
    }

    // Limit to 3 digits before and after the slash
    const parts = input.split('/');
    if (parts[0]) {
      parts[0] = parts[0].replace(/^0+/, '');
    }
    if (parts[0]?.length > 3) {
      parts[0] = parts[0].slice(0, 3);
    }
    if (parts[1]) {
      parts[1] = parts[1].replace(/^0+/, '');
    }
    if (parts[1]?.length > 3) {
      parts[1] = parts[1].slice(0, 3);
    }

    // Join the parts back together
    const formattedValue = parts.join('/');

    // Pass the formatted value to the parent component
    onChange(formattedValue);
  };


  const handleKeyUp = (e) => {
    const maxLength = mask.replace(/\D/g, '').length;
    if (e.target.value.replace(/\D/g, '').length >= maxLength) {
      const nextInput = document.querySelector('[name="HeartRate"]');
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
        <Activity size={20} />
      </span>
      <Form.Control
        type="text"
        inputMode="numeric"
        placeholder="___/___"
        maxLength={7}
        className={`vitals-input-with-icon ${fieldErrors?.BloodPressure ? 'error-border' : ''}`}
        value={value}
        onChange={handleChange}
        isInvalid={isInvalid}
        style={fieldErrors?.BloodPressure ? inputErrorStyle : {}}
      /> 
    </div>
  );
}

const VitalsMetabolicsModal = ({ show, handleClose, rowData, isVitals, onUpdateSuccess, selectedFamilyMember }) => {
  const dispatch = useDispatch();
  const loggedInUserId = useSelector((state) => state.apiService?.login?.user_id);
  const userId = selectedFamilyMember?.user_id || loggedInUserId;
  const token = useSelector((state) => state.apiService?.token);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [congratulationsData, setCongratulationsData] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const inputErrorStyle = { border: '1px solid #dc3545', boxShadow: 'none' };
  const heartRateRef = useRef(null);
  const bloodSugarRef = useRef(null);
  const weightRef = useRef(null);
  const waistSizeRef = useRef(null);
  const datePickerRef = useRef(null);

  const { 
    register, 
    watch, 
    setValue, 
    reset,
    getValues,
    formState: { errors } 
  } = useForm({
    defaultValues: {
      BloodPressure: "",
      HeartRate: "",
      BloodSugar: "",
      Weight: "",
      WaistSize: "",
      LastUpdated: new Date(),
      VitalID: "",
      LoginUserID: userId
    }
  });

  useEffect(() => {
    if (show) {
      setFieldErrors({});
    }
  }, [show]);

  useEffect(() => {
    // Helper to handle NA/null/undefined
    const safe = (val) => (val === 'NA' || val === null || val === undefined ? '' : val);
    if (rowData) {
      let patchValues = {};
      // Fix date handling for existing data (force to local date, strip time)
      let lastUpdated;
      if (rowData.LastUpdated) {
        // Always treat as UTC and convert to local date (strip time)
        const d = new Date(rowData.LastUpdated);
        lastUpdated = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      } else {
        const now = new Date();
        lastUpdated = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      }
      if (rowData.isVitals) {
        // Vitals edit
        patchValues = {
          BloodPressure: safe(rowData.BloodPressure_Systolic) && safe(rowData.BloodPressure_Diastolic)
            ? `${safe(rowData.BloodPressure_Systolic)}/${safe(rowData.BloodPressure_Diastolic)}`
            : '',
          HeartRate: safe(rowData.HeartRate),
          BloodSugar: '',
          Weight: '',
          WaistSize: '',
          LastUpdated: lastUpdated, // Only date, no time
          VitalID: rowData.VitalID || '',
          LoginUserID: userId
        }; 
      } else {
        // Metabolics edit
        patchValues = {
          BloodPressure: '',
          HeartRate: '',
          BloodSugar: safe(rowData.BloodSugar),
          Weight: safe(rowData.Weight),
          WaistSize: safe(rowData.WaistSize),
          LastUpdated: lastUpdated, // Only date, no time
          VitalID: rowData.VitalID || '',
          LoginUserID: userId
        };
      }
      reset(patchValues);
    } else {
      const now = new Date();
      reset({
        BloodPressure: "",
        HeartRate: "",
        BloodSugar: "",
        Weight: "",
        WaistSize: "",
        LastUpdated: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
        VitalID: "",
        LoginUserID: userId
      });
    }
  }, [rowData, reset, userId]);

  const onChangeInput = (currentInput, nextInput, maxLength = 2) => {
    if (currentInput.current && currentInput.current.value.length >= maxLength) {
      setTimeout(() => {
        if (nextInput) {
          if (nextInput === datePickerRef) {
            datePickerRef.current.flatpickr.open();
          } else if (nextInput.current && typeof nextInput.current.focus === 'function') {
            nextInput.current.focus();
          }
        }
      }, 200);
    }
  };

  const celebrate = () => {
    const duration = 8000;
    confetti({
      particleCount: 650,
      spread: 480,
      origin: { y: 0.6 },
      colors: ['#FF4500', '#008080', '#FFD700'],
      shapes: ['text'],
    });
    setTimeout(() => confetti.reset(), duration);
  };

  const onSaveClick = async (data) => {
    try {
      // Validation with field errors (same logic as allergies)
      const errors = {};
      if (isVitals) {
        const bp = data.BloodPressure?.trim();
        if (!bp || !bp.includes('/')) {
          errors.BloodPressure = true;
        } else {
          const bpParts = bp.split('/');
          if (
            bpParts.length !== 2 ||
            !bpParts[0] ||
            !bpParts[1] ||
            isNaN(Number(bpParts[0])) ||
            isNaN(Number(bpParts[1]))
          ) {
            errors.BloodPressure = true;
          }
        }
        errors.HeartRate = !data.HeartRate?.toString().trim();
      } else {
        errors.BloodSugar = !data.BloodSugar?.toString().trim();
        errors.Weight = !data.Weight?.toString().trim();
        errors.WaistSize = !data.WaistSize?.toString().trim();
      }
      setFieldErrors(errors);
      if (errors.BloodPressure || errors.HeartRate || errors.BloodSugar || errors.Weight || errors.WaistSize) return;

      // Fix date handling: always send only the date part in UTC (no time)
      let lastUpdated;
      if (data.LastUpdated) {
        // Convert to UTC midnight
        const d = new Date(data.LastUpdated);
        const now = new Date();
        lastUpdated = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), now.getHours(), now.getMinutes())).toISOString(); // 'YYYY-MM-DD'
      } else {
        const now = new Date();
        lastUpdated = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes())).toISOString();
      }

      // Prepare payload
      const payload = {
        LoginUserID: userId,
        token: token,
        VitalID: data.VitalID || "",
        LastUpdated: lastUpdated, // Only date string 'YYYY-MM-DD'
        BloodPressure_Systolic: isVitals ? (data.BloodPressure.split('/')[0] || '') : '',
        BloodPressure_Diastolic: isVitals ? (data.BloodPressure.split('/')[1] || '') : '',
        HeartRate: isVitals ? data.HeartRate : '',
        BloodSugar: !isVitals ? data.BloodSugar : '',
        Weight: !isVitals ? data.Weight : '',
        WaistSize: !isVitals ? data.WaistSize : '',
        ...(isVitals
          ? { BloodSugar: '', Weight: '', WaistSize: '' }
          : { BloodPressure_Systolic: '', BloodPressure_Diastolic: '', HeartRate: '' }
        )
      }; 
      // Call the API
      const saveAction = isVitals ? VitalsSave : MetabolicsSave;
      const result = await dispatch(saveAction(payload)).unwrap();
      if (result.status === 'success' || result.status === true) {
        if (isVitals) {
          // showToast('success', data.VitalID ? 'Vitals updated successfully!' : 'Vitals added successfully!');
        } else {
          // showToast('success', data.VitalID ? 'Metabolics updated successfully!' : 'Metabolics added successfully!');
        }
        if (onUpdateSuccess) onUpdateSuccess();
        if (result.Table && result.Table.length > 0 || result.Table1 && result.Table1.length > 0) {
          setCongratulationsData(result); 
          setShowCongratulations(true);
          celebrate();
        }
        handleClose();
      }
    } catch (error) {
      console.error('Error saving data:', error); 
    }
  };

  const flatpickrOptions = {
    altInput: true,
    maxDate: new Date(),
    enableTime: false, // Do not allow time selection
    noCalendar: false,
    dateFormat: 'Y-m-d', // Only date for backend
    altFormat: 'm/d/Y', // Only date for user
    defaultDate: new Date()
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered size="sm" dialogClassName="upcoming-appointments-modal" backdrop="static" keyboard={false} enforceFocus={false}>
        <Modal.Header closeButton>
          <Modal.Title className="text-capitalize">
            {rowData?.VitalID
              ? isVitals ? 'Edit Vitals' : 'Edit Metabolics'
              : isVitals ? 'Add Vitals' : 'Add Metabolics'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form autoComplete="off">
            {isVitals ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className='fs-6'>Blood Pressure(Systolic/Diastolic) (mmHg)</Form.Label>
                  <VitalsBPField 
                    value={watch("BloodPressure") || ""} 
                    onChange={(value) => {
                      setValue("BloodPressure", value);
                      setFieldErrors((prev) => ({ ...prev, BloodPressure: false }));
                    }}
                    // isInvalid={!!fieldErrors.BloodPressure}
                    fieldErrors={fieldErrors}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className='fs-6'>Heart Rate (bpm)</Form.Label>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                      <Heart size={20} />
                    </span>
                    <Form.Control
                      {...register("HeartRate", { required: isVitals })}
                      className={`vitals-input-with-icon ${fieldErrors?.HeartRate ? 'error-border' : ''}`}
                      name="HeartRate"
                      ref={heartRateRef}
                      style={{ 
                        paddingLeft: 40,
                        ...(fieldErrors?.HeartRate ? inputErrorStyle : {})
                      }}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={watch("HeartRate") || ""}
                      onChange={(e) => {
                        // Only allow numbers
                        const numericValue = e.target.value.replace(/[^0-9]/g, '').replace(/^0+/, '');
                        setValue("HeartRate", numericValue);
                        setFieldErrors((prev) => ({ ...prev, HeartRate: false }));
                      }}
                    /> </div>
                </Form.Group>
              </>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className='fs-6'>Blood Sugar (mg/dL)</Form.Label>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                      <Droplet size={20} />
                    </span>
                    <Form.Control
                      className={`vitals-input-with-icon ${fieldErrors?.BloodSugar ? 'error-border' : ''}`}
                      {...register("BloodSugar", { 
                        required: !isVitals,
                        valueAsNumber: true
                      })}
                      ref={bloodSugarRef}
                      style={{ 
                        paddingLeft: 40,
                        ...(fieldErrors?.BloodSugar ? inputErrorStyle : {})
                      }}
                      // isInvalid={!!fieldErrors.BloodSugar}
                      value={watch("BloodSugar") || ""} 
                      onChange={(e) => {
                        setValue("BloodSugar", e.target.value);
                        setFieldErrors((prev) => ({ ...prev, BloodSugar: false }));
                      }}
                    />  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className='fs-6'>Weight (lbs)</Form.Label>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                      <Database size={20} />
                    </span>
                    <Form.Control
                      className={`vitals-input-with-icon ${fieldErrors?.Weight ? 'error-border' : ''}`}
                      {...register("Weight", { 
                        required: !isVitals,
                        valueAsNumber: true
                      })}
                      ref={weightRef}
                      style={{ 
                        paddingLeft: 40,
                        ...(fieldErrors?.Weight ? inputErrorStyle : {})
                      }}
                      //isInvalid={!!fieldErrors.Weight}
                      value={watch("Weight") || ""}
                      onChange={(e) => {
                        setValue("Weight", e.target.value);
                        setFieldErrors((prev) => ({ ...prev, Weight: false }));
                      }}
                    />  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className='fs-6'>Waist Size (inches)</Form.Label>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                      <Underline size={20} />
                    </span>
                    <Form.Control
                      className={`vitals-input-with-icon ${fieldErrors?.WaistSize ? 'error-border' : ''}`}
                      {...register("WaistSize", { 
                        required: !isVitals,
                        valueAsNumber: true
                      })}
                      ref={waistSizeRef}
                      style={{ 
                        paddingLeft: 40,
                        ...(fieldErrors?.WaistSize ? inputErrorStyle : {})
                      }}
                     // isInvalid={!!fieldErrors.WaistSize}
                      value={watch("WaistSize") || ""}
                      onChange={(e) => {
                        setValue("WaistSize", e.target.value);
                        setFieldErrors((prev) => ({ ...prev, WaistSize: false }));
                      }}
                    />  </div>
                </Form.Group>
              </>
            )}

            <Form.Group className="mb-3">
              <Form.Label className='fs-6'>Reported On</Form.Label>
              <div style={{ position: "relative" }}>
                <Flatpickr
                  options={flatpickrOptions}
                  className="form-control"
                  onChange={([date]) => setValue("LastUpdated", date)}
                  value={watch("LastUpdated")}
                  placeholder="mm/dd/yyyy"
                  style={{ paddingRight: 40 }}
                  ref={datePickerRef}
                  fieldErrors={fieldErrors}
                />
                <span
                  style={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "var(--pmr-text-muted)",
                    height: "36px",
                    display: "flex",
                    alignItems: "center"
                  }}
                  onClick={() => datePickerRef.current.flatpickr.open()}
                >
                  <Calendar size={20} />
                </span>
              </div>
            </Form.Group>

            <div className="text-center">
              <Button
                variant="primary"
                className="me-2 text-capitalize fw-bold "
                onClick={async () => {
                  const data = getValues();
                  await onSaveClick(data);
                }}
                // style={{
                //  padding: '10px 20px',
                //  borderRadius: '5px',
                //  backgroundColor: '#003b87',
                //  borderColor: '#003b87',
                //  color: '#fff',
                //  fontWeight: 'bold',
                //  textTransform: 'capitalize',
                // }}
              >
                {rowData?.VitalID ? 'Update' : 'Save'}
              </Button>
              <Button variant="danger" className='text-capitalize fw-bold' onClick={handleClose}
              // style={{
              //   padding: '10px 20px',
              //   borderRadius: '5px',
              //   backgroundColor: '#dc3545',
              //   borderColor: '#dc3545',
              //   color: '#fff',
              //   fontWeight: 'bold',
              //   textTransform: 'capitalize',
              // }}
                >Close</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <CongratulationsModal
        show={showCongratulations}
        handleClose={() => setShowCongratulations(false)}
        modelRefData={congratulationsData}
        onClose={() => setShowCongratulations(false)}
      />
      
      <style>{`.error-border { border: 1px solid #dc3545 !important; box-shadow: none !important; }`}</style>
    </>
  );
};

export default VitalsMetabolicsModal;